import { Component } from '@theme/component';
import { VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that manages variant dropdown selection on collection pages.
 * Handles variant selection, updates the product form, and communicates with
 * the add-to-cart button.
 *
 * @extends Component
 */
export class VariantDropdownComponent extends Component {
  /** @type {object[]} */
  #variants = [];

  /** @type {object[]} */
  #options = [];

  /** @type {HTMLSelectElement[]} */
  #selects = [];

  /** @type {string | null} */
  #selectedVariantId = null;

  /** @type {HTMLElement | null} */
  #addToCartComponent = null;

  /** @type {HTMLInputElement | null} */
  #variantIdInput = null;

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    // Parse variant data from JSON script
    const variantDataScript = this.querySelector('script[data-variant-data]');
    if (variantDataScript) {
      try {
        const data = JSON.parse(variantDataScript.textContent || '{}');
        this.#variants = data.variants || [];
        this.#options = data.options || [];
      } catch (error) {
        console.error('Failed to parse variant data:', error);
      }
    }

    // Get all select elements
    this.#selects = Array.from(this.querySelectorAll('.variant-option__select'));

    // Find any product form in the product card (works with buy-buttons or other add-to-cart blocks)
    const productCard = this.closest('.product-card') || this.closest('[data-product-id]');
    if (productCard) {
      // Look for product-form-component (used by buy-buttons and other blocks)
      const productForm = /** @type {HTMLElement | null} */ (
        productCard.querySelector('product-form-component')
      );
      if (productForm) {
        this.#addToCartComponent = productForm;
        // Find the variant ID input (standard across all product forms)
        this.#variantIdInput = /** @type {HTMLInputElement | null} */ (
          productForm.querySelector('input[name="id"]')
        );
      }
    }

    // Set up event listeners
    this.#selects.forEach((select) => {
      select.addEventListener('change', this.#handleVariantChange.bind(this));
    });

    // Initialize with first variant
    this.#updateSelectedVariant();
  }

  /**
   * Handles variant selection change
   * @param {Event} event - The change event
   */
  #handleVariantChange(event) {
    if (!(event.target instanceof HTMLSelectElement)) return;
    const changedSelect = event.target;
    const optionPosition = parseInt(changedSelect.dataset.optionPosition || '1');

    // If this is the first option and there are multiple options,
    // update the available options in dependent dropdowns
    if (optionPosition === 1 && this.#selects.length > 1) {
      this.#updateDependentOptions();
    }

    // Update the selected variant
    this.#updateSelectedVariant();
  }

  /**
   * Updates dependent dropdown options based on first option selection
   */
  #updateDependentOptions() {
    if (this.#selects.length < 2) return;

    const firstSelect = this.#selects[0];
    if (!firstSelect) return;
    const selectedFirstOption = firstSelect.value;

    // For each subsequent dropdown
    for (let i = 1; i < this.#selects.length; i++) {
      const dependentSelect = this.#selects[i];
      if (!dependentSelect) continue;
      const dependentPosition = parseInt(dependentSelect.dataset.optionPosition || '1');
      const currentValue = dependentSelect.value;

      // Find available options for this position given the selected first option
      const availableOptions = new Set();

      this.#variants.forEach((variant) => {
        // @ts-ignore - Shopify variant object has option1, option2, etc properties
        if (variant[`option${1}`] === selectedFirstOption) {
          // @ts-ignore - Shopify variant object has option1, option2, etc properties
          availableOptions.add(variant[`option${dependentPosition}`]);
        }
      });

      // Update option availability
      Array.from(dependentSelect.options).forEach((option) => {
        const optionValue = option.value;
        const isAvailable = availableOptions.has(optionValue);

        // Check if any variant with this combination is actually available (in stock)
        let hasAvailableStock = false;
        if (isAvailable) {
          const matchingVariant = this.#findMatchingVariant({
            1: selectedFirstOption,
            [dependentPosition]: optionValue
          });
          // @ts-ignore - Shopify variant object has available property
          hasAvailableStock = matchingVariant?.available || false;
        }

        option.dataset.available = hasAvailableStock.toString();

        // Update option text to show unavailability
        const baseText = optionValue;
        if (!hasAvailableStock && !option.textContent?.includes('Unavailable')) {
          option.textContent = `${baseText} - Unavailable`;
        } else if (hasAvailableStock && option.textContent?.includes('Unavailable')) {
          option.textContent = baseText;
        }
      });

      // If current selection is no longer available, select first available option
      const currentOption = Array.from(dependentSelect.options).find(opt => opt.value === currentValue);
      if (!currentOption || currentOption.dataset.available === 'false') {
        const firstAvailable = Array.from(dependentSelect.options).find(
          opt => opt.dataset.available === 'true'
        );
        if (firstAvailable) {
          dependentSelect.value = firstAvailable.value;
        }
      }
    }
  }

  /**
   * Finds a matching variant based on selected options
   * @param {object | null} selectedOptions - Object mapping option positions to values
   * @returns {object | null} - The matching variant or null
   */
  #findMatchingVariant(selectedOptions = null) {
    // Get current selections if not provided
    if (!selectedOptions) {
      selectedOptions = {};
      this.#selects.forEach((select) => {
        const position = parseInt(select.dataset.optionPosition || '1');
        // @ts-ignore - Building dynamic object
        selectedOptions[position] = select.value;
      });
    }

    // Find matching variant
    return this.#variants.find((variant) => {
      return Object.keys(selectedOptions || {}).every((position) => {
        // @ts-ignore - Shopify variant object has option1, option2, etc properties
        return variant[`option${position}`] === selectedOptions[position];
      });
    }) || null;
  }

  /**
   * Gets the selected option value IDs for the current selections
   * @returns {string[]} Array of option value IDs
   */
  #getSelectedOptionsValues() {
    return this.#selects.map(select => {
      const selectedOption = /** @type {HTMLOptionElement | null} */ (select.querySelector('option:checked'));
      return selectedOption?.dataset.optionValueId || '';
    }).filter(Boolean);
  }

  /**
   * Gets the first selected option value ID (for event dispatching)
   * @returns {string | undefined} The first option value ID
   */
  #getFirstSelectedOptionId() {
    const firstSelect = this.#selects[0];
    const selectedOption = /** @type {HTMLOptionElement | null} */ (firstSelect?.querySelector('option:checked'));
    return selectedOption?.dataset.optionValueId;
  }

  /**
   * Updates the selected variant and form state
   */
  #updateSelectedVariant() {
    const matchingVariant = this.#findMatchingVariant();

    if (matchingVariant) {
      // @ts-ignore - Shopify variant object has id property
      this.#selectedVariantId = matchingVariant.id?.toString() || '';

      // Update the hidden variant ID input
      if (this.#variantIdInput && this.#selectedVariantId) {
        this.#variantIdInput.value = this.#selectedVariantId;

        // Enable/disable based on availability
        // @ts-ignore - Shopify variant object has available property
        if (matchingVariant.available) {
          this.#variantIdInput.disabled = false;
          this.#enableAddToCart();
        } else {
          this.#variantIdInput.disabled = true;
          this.#disableAddToCart();
        }
      }

      // Fetch updated product HTML and dispatch standard event
      this.#fetchAndDispatchUpdate();
    } else {
      // No matching variant
      this.#selectedVariantId = null;
      if (this.#variantIdInput) {
        this.#variantIdInput.disabled = true;
      }
      this.#disableAddToCart();
    }
  }

  /**
   * Fetches updated product HTML and dispatches VariantUpdateEvent
   * This matches the pattern used by variant-picker.js
   */
  #fetchAndDispatchUpdate() {
    // Abort any pending request
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const productUrl = this.dataset.productUrl?.split('?')[0];
    if (!productUrl) return;

    const selectedOptionsValues = this.#getSelectedOptionsValues();
    if (selectedOptionsValues.length === 0) return;

    const requestUrl = `${productUrl}?section_id=section-rendering-product-card&option_values=${selectedOptionsValues.join(',')}`;

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        
        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        const selectedOptionId = this.#getFirstSelectedOptionId();

        // Dispatch the standard VariantUpdateEvent that product-card.js listens for
        if (selectedOptionId) {
          this.dispatchEvent(
            new VariantUpdateEvent(JSON.parse(textContent), selectedOptionId, {
              html,
              productId: this.dataset.productId ?? '',
            })
          );
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error('Failed to fetch variant data:', error);
        }
      });
  }

  /**
   * Enables the add-to-cart button
   */
  #enableAddToCart() {
    if (!this.#addToCartComponent) return;

    const addToCartButton = /** @type {HTMLButtonElement | null} */ (
      this.#addToCartComponent.querySelector('button[type="submit"]')
    );
    if (addToCartButton) {
      addToCartButton.disabled = false;
    }
  }

  /**
   * Disables the add-to-cart button
   */
  #disableAddToCart() {
    if (!this.#addToCartComponent) return;

    const addToCartButton = /** @type {HTMLButtonElement | null} */ (
      this.#addToCartComponent.querySelector('button[type="submit"]')
    );
    if (addToCartButton) {
      addToCartButton.disabled = true;
    }
  }

  /**
   * Gets the currently selected variant ID
   * @returns {string | null}
   */
  getSelectedVariantId() {
    return this.#selectedVariantId;
  }
}

// Register custom element
if (!customElements.get('variant-dropdown-component')) {
  customElements.define('variant-dropdown-component', VariantDropdownComponent);
}
